import { FileVideo, ImageIcon, Megaphone } from 'lucide-react';

import { Skeleton } from '@/components/ui/skeleton';

import { cn } from '@/lib/utils';
import type { OpenArtifact } from './artifact-panel-context';
import { useArtifactPanel } from './artifact-panel-context';
import { useImageLoaded } from './image-reveal';
import { useSelectMediaVersion } from './media-version-selection';

interface ArtifactRowProps {
  /**
   * What this row points at. Taken whole rather than as `onOpen`, because the
   * row needs the identity for TWO things — opening the panel and knowing
   * whether it is the one currently open — and a callback only covers the
   * first. Every caller would otherwise have to compare ids itself and get the
   * highlight subtly wrong in a different way.
   */
  artifact: OpenArtifact;
  title: string;
  /** "Video · MP4", "Graphic · PNG" — what it is, then the format. */
  subtitle: string;
  /** Still frame, when there is one. Falls back to the kind's icon. */
  thumbnailUrl?: string | null;
  /**
   * Still being made. Pulses the thumbnail so the row reads as in-flight
   * without repeating the progress the panel is already showing at full size.
   */
  pending?: boolean;
  /** Right-hand action, e.g. a download. Optional. */
  action?: React.ReactNode;
  className?: string;
  /**
   * Never show this row as "selected", even if `artifact.id` matches the
   * panel's currently open one.
   *
   * A graphic edit is commonly staged on the SAME still-unrendered id across
   * several turns before it ever renders, so more than one row in the
   * transcript can legitimately share an id — without this, every one of
   * them lights up whenever any of them is opened. The caller is expected to
   * set this on every row EXCEPT the newest one sharing that id.
   */
  disableSelectedHighlight?: boolean;
}

/**
 * A finished render, as a row in the conversation.
 *
 * The chat is a transcript, and a video player embedded in one is a poor
 * citizen of it: it is the tallest thing on the page, it scrolls away, and
 * every earlier render stays mounted and playable above the current one. A row
 * is a REFERENCE — it says the thing exists, names it, and opens it.
 *
 * The whole row is the target rather than a "View" button. The row is a single
 * object and there is one thing to do with it; a button would be a smaller
 * target making the same claim, and the rest of the row would look inert while
 * being the obvious place to click.
 */
export function ArtifactRow({
  artifact,
  title,
  subtitle,
  thumbnailUrl,
  pending,
  action,
  className,
  disableSelectedHighlight,
}: ArtifactRowProps) {
  const { artifact: open, openArtifact } = useArtifactPanel();
  const selectVersion = useSelectMediaVersion();
  const Icon =
    artifact.kind === 'video'
      ? FileVideo
      : artifact.kind === 'ad'
        ? Megaphone
        : ImageIcon;
  // Which one am I looking at. A transcript accumulates rows — every edit adds
  // one — and without this the panel shows a video with no way to tell which
  // of five rows produced it. `disableSelectedHighlight` overrides this to
  // `false` for a row whose id is shared with a NEWER row (see the prop doc).
  const isOpen =
    !disableSelectedHighlight &&
    open?.kind === artifact.kind &&
    open.id === artifact.id;
  const thumbnail = useImageLoaded(thumbnailUrl ?? undefined);
  const thumbnailLoaded = thumbnail.loaded;

  return (
    <div
      className={cn(
        // Fades in on mount, so the row replacing its own skeleton (or
        // arriving with a turn) eases in rather than snapping into place.
        'flex animate-in items-center gap-3 rounded-xl border bg-card p-2.5 transition-colors fade-in-0 duration-300 hover:bg-accent/50 motion-reduce:animate-none',
        isOpen && 'border-primary bg-accent/40 ring-1 ring-primary/40',
        className
      )}
    >
      <button
        type="button"
        onClick={() => {
          // Versions are a property of rendered MEDIA on a content item; an
          // ad has no item and no version list, so there is nothing to select.
          if (artifact.kind !== 'ad' && artifact.itemId) {
            selectVersion?.({
              itemId: artifact.itemId,
              kind: artifact.kind,
              assetId: artifact.id,
            });
          }
          openArtifact(artifact);
        }}
        aria-current={isOpen ? 'true' : undefined}
        className="flex min-w-0 flex-1 items-center gap-3 text-left"
      >
        {/* The thumbnail fills a box that is already there: the icon stays
            underneath and the still fades in over it, blurred then sharp,
            once its bytes have arrived — rather than the icon being swapped
            for an image that pops in a beat later. */}
        <span
          className={cn(
            'relative grid size-11 shrink-0 place-items-center overflow-hidden rounded-lg border bg-muted',
            pending && !thumbnailLoaded && 'animate-pulse'
          )}
        >
          <Icon
            className={cn(
              'size-5 text-muted-foreground transition-opacity duration-300',
              thumbnailLoaded && 'opacity-0'
            )}
          />
          {/* No `key` on the URL: a re-signed URL for the same still must
              swap the src in place, not remount the element and replay the
              fade on every poll. */}
          {thumbnailUrl ? (
            <img
              src={thumbnailUrl}
              alt=""
              ref={thumbnail.ref}
              onLoad={thumbnail.onLoad}
              className={cn(
                'absolute inset-0 size-full object-cover transition-[opacity,filter] duration-500 motion-reduce:transition-none',
                thumbnailLoaded ? 'opacity-100 blur-0' : 'opacity-0 blur-md'
              )}
            />
          ) : null}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium">{title}</span>
          <span className="block truncate text-xs text-muted-foreground">
            {subtitle}
          </span>
        </span>
      </button>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}

/**
 * The row's shape before there is a row.
 *
 * A tool that produces an artifact takes seconds to answer, and until it does
 * the transcript used to show nothing where the artifact would land — the
 * owner read the trace line or nothing. This is the same footprint as
 * `ArtifactRow`, pulsing, so the thing that is coming already has its place in
 * the conversation and the layout does not jump when it arrives.
 *
 * It is `role="status"` with the label as its text, so a screen reader hears
 * "Creating ad…" rather than an unlabelled pulsing box.
 */
export function ArtifactRowSkeleton({
  label,
  className,
}: {
  /** "Creating ad…" — what is being made. */
  label: string;
  className?: string;
}) {
  return (
    <div
      role="status"
      aria-label={label}
      data-testid="artifact-row-skeleton"
      className={cn(
        'flex items-center gap-3 rounded-xl border bg-card p-2.5',
        className
      )}
    >
      <Skeleton className="size-11 shrink-0 rounded-lg" />
      <span className="flex min-w-0 flex-1 flex-col gap-1.5">
        <Skeleton className="h-3.5 w-2/5" />
        <Skeleton className="h-3 w-1/4" />
      </span>
      <span className="shrink-0 text-xs text-muted-foreground">{label}</span>
    </div>
  );
}
